import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { DefaultEditableActionItemContentCSS } from '../../../../models';
import { InputFieldError } from '../../../components/InputFieldError';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../components/richContent/RichContentDocumentEditor';
import { titles } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';

const DefaultActionItemEditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	contentRawCss: DefaultEditableActionItemContentCSS.replace(/color:[ ]?[#]?[a-z0-9]*;/i, `color: ${titles};`),
	minHeight: 14,
	plugins: [],
	toolbar: false,
};

/**
 * ActionItemStepForm component.
 *
 * Renders the form for editing an action item automation step.
 * Handles the action item content editor with validation and error display.
 *
 * @param actionItemContentEditorState - Current rich content editor state
 * @param hasContent - Whether the action item has content
 * @param readonly - Whether the form is read-only
 * @param showRequiredFieldError - Whether to show required field errors
 * @param baseKey - Base key for React component keys
 * @param onContentStateChanged - Handler for editor content changes
 * @param onSave - Handler to trigger save
 * @param onClearError - Handler to clear error state
 */
interface ActionItemStepFormProps {
	actionItemContentEditorState: Api.IRichContentEditorState | null;
	hasContent: boolean;
	readonly: boolean;
	showRequiredFieldError: boolean;
	baseKey: string;
	onContentStateChanged: (newState: Api.IRichContentEditorState) => void;
	onSave: () => void;
	onClearError: () => void;
}

export function ActionItemStepForm({
	actionItemContentEditorState,
	hasContent,
	readonly,
	showRequiredFieldError,
	baseKey,
	onContentStateChanged,
	onSave,
	onClearError,
}: ActionItemStepFormProps) {
	let errorMessage = null;
	if (showRequiredFieldError && !hasContent) {
		errorMessage = 'Please provide action item content.';
	}

	return (
		<>
			<div className={css(styleSheet.fieldLabel, baseStyleSheet.mb3, baseStyleSheet.mt3, baseStyleSheet.textXs)}>
				Type action item below:
			</div>
			<InputFieldError errorMessage={errorMessage}>
				<RichContentDocumentEditor
					config={DefaultActionItemEditorConfig}
					contentPlaceholderText='Type action item...'
					contentState={actionItemContentEditorState}
					key={`${baseKey}-action-item-content`}
					onBlur={onSave}
					onContentStateChanged={onContentStateChanged}
					onFocus={onClearError}
					readOnly={readonly}
					styles={[styleSheet.actionItemContentEditor]}
				/>
			</InputFieldError>
		</>
	);
}
